/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { PlanId } from "components/common/boundary/PlanId";
import { CommentCell } from "components/common/plan/CommentCell";
import { PopoverLayerEditor } from "components/common/plan/columns";
import { DeployTable } from "components/common/plan/DeployTable";
import { LayerEditor } from "components/common/plan/layerEditor/LayerEditor";
import { renderMixPriceComparison } from "components/common/plan/MixSummaryCell";
import { EditableTableSkeleton } from "components/common/plan/EditableTableSkeleton";
import { SteelGradesSelect } from "components/common/plan/SteelGradesSelect";
import { Row } from "components/common/plan/types";
import {
  filterPlanResultsToPeriod,
  makeRows,
  tableRowsToBackendMixes,
} from "components/common/plan/utils";
import { PlanAncestorsTooltip } from "components/common/planHistory/PlanAncestorsTooltip";
import { SteelGrade } from "contexts/search/context";
import { useTenantData } from "contexts/tenant";
import equal from "fast-deep-equal";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { Period } from "hooks/periodIndex";
import { useSearchId } from "hooks/search";
import { useTenant } from "hooks/settings";
import { useMixGrouping } from "hooks/useMixGrouping";
import { MixCollapse, useMixCollapse } from "hooks/useMixCollapse";
import { useMixQuantity } from "hooks/useMixQuantity.tsx";
import { usePeriodNameFormatter } from "hooks/usePeriodNameFormatter";
import React from "react";
import {
  generatedApi,
  MaterialRead,
  PeriodSummary,
  PlannedResultsRead,
  ProductionMixResultRead,
  useGetPlanQuery,
  useSummarisePlanResultsMutation,
} from "src/store/api/generatedApi";
import { deployPlanFormSlice } from "src/store/slices/deployPlanFormSlice";
import {
  makePlanPeriodId,
  parsePlanPeriodId,
  PlanMixTableEdits,
  planMixTableEditsSlice,
  PlanPeriodId,
  selectLoadedPlanResults,
  selectPeriodPlanEdits,
} from "src/store/slices/planMixesTableEditSlice";
import { selectSearchedSteelGradeIds } from "src/store/slices/steelGradeSearchSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { typeSafeObjectEntries } from "src/utils/typeSafeObjectEntries";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import { typeSafeObjectKeys } from "src/utils/typeSafeObjectKeys";
import { useLayerEditor } from "./useLayerEditor";
import {
  filterPeriodPlanEdits,
  getMixIdsRequiringMixCodeOverride,
  getPlanPeriodKeys,
  unpackEdits,
} from "./utils";

const PERCENTAGE_SCALE_FACTOR = 100;

interface Props {
  preview: {
    planId: PlanId;
    period: Period;
    title: string;
    isBackup: boolean;
  } | null;
  periods: PeriodSummary[];
  allBackupPlanIds: PlanId[];
  planId: PlanId;
  setPlanId: (planId: PlanId) => void;
  materials: MaterialRead[];
  steelGrades: SteelGrade[];
}
interface ContentWrapperProps {
  preview: {
    planId: PlanId;
    period: Period;
    title: string;
    isBackup: boolean;
  };
  planId: PlanId;
  setPlanId: (planId: PlanId) => void;
  materials: MaterialRead[];
  steelGrades: SteelGrade[];
  planPeriodKeys: PlanPeriodId[];
  loadedPlanResults: Record<PlanPeriodId, PlannedResultsRead>;
}

type ContentProps = Omit<
  ContentWrapperProps & {
    periodPlanEdits: Record<PlanPeriodId, PlanMixTableEdits>;
  },
  "planPeriodKeys"
>;

interface DeployTableRenderArgs {
  onChange: (planId: PlanId, period: Period, editedRows: Row[]) => void;
  isSubmitting: boolean;
  hasEdited: boolean;
  rows: Row[];
  massScaleFactor: number;
  isPercentageMode: boolean;
  popoverLayerEditor: PopoverLayerEditor | undefined;
  collapse: MixCollapse;
}

interface TableWrapperProps {
  formPlanId: PlanId;
  focussedPlanId: PlanId;
  focussedPeriod: Period;
  setPlanId: (planId: PlanId) => void;
  materials: MaterialRead[];
  allSteelGrades: SteelGrade[];
  focussedSteelGrades: SteelGrade[];
  results: Record<PlanPeriodId, ProductionMixResultRead[]>;
  children: (args: DeployTableRenderArgs) => React.JSX.Element;
  isSubmitting: boolean;
  originalRows: Record<PlanPeriodId, Row[]>;
  editedRows: Record<PlanPeriodId, Row[]>;
  focussedEditedRows: Row[];
}

export const EditableDeployForm = ({
  preview,
  periods,
  allBackupPlanIds,
  planId,
  setPlanId,
  materials,
  steelGrades,
}: Props) => {
  const periodNameFormatter = usePeriodNameFormatter();
  const dispatch = useAppDispatch();
  const tenantName = useTenant();

  React.useLayoutEffect(() => {
    const [period] = periods;
    if (!preview && period) {
      dispatch(
        deployPlanFormSlice.actions.addPeriod({
          planId,
          period: period.period as Period,
        })
      );
      dispatch(
        deployPlanFormSlice.actions.setPreview([
          planId,
          {
            planId,
            period: period.period as Period,
            title: periodNameFormatter(period.period as Period, {
              startTimestamp: period.start_timestamp,
              endTimestamp: period.end_timestamp,
              name: period.name,
            }),
            isBackup: false,
          },
        ])
      );
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- planId and periodNameFormatter are used inside the effect but should not re-trigger the layout effect; periods already captures the relevant changes
  }, [periods, preview, dispatch]);

  const planPeriodKeys = React.useMemo(
    () => getPlanPeriodKeys(periods, allBackupPlanIds, planId),
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- planId is excluded because adding it would recompute keys when only the plan changes, not the periods/backups
    [periods, allBackupPlanIds]
  );

  const loadedPlanResults = useAppSelector((state) =>
    selectLoadedPlanResults(state, tenantName, planPeriodKeys)
  );

  const [getPlanResultsBatch] = generatedApi.useGetPlanResultsBatchMutation();

  const fetchedKeysRef = React.useRef(new Set());

  React.useEffect(() => {
    const requests: { plan_id: number; period: number }[] = [];

    planPeriodKeys.forEach((planPeriodId) => {
      if (fetchedKeysRef.current.has(planPeriodId)) {
        return;
      }

      const [planId, period] = parsePlanPeriodId(planPeriodId);
      requests.push({ plan_id: planId, period: period });
      fetchedKeysRef.current.add(planPeriodId);
    });

    if (requests.length > 0) {
      void getPlanResultsBatch({ tenantName, requests })
        .unwrap()
        .then((response) => {
          response.results.forEach((item) => {
            if (item.response) {
              void dispatch(
                generatedApi.util.upsertQueryData(
                  "getPlanResults",
                  {
                    tenantName,
                    planId: item.plan_id,
                    period: item.period,
                  },
                  filterPlanResultsToPeriod(item.response, item.period)
                )
              );
            }
          });
        })
        .catch((error: unknown) => {
          console.error("Failed to fetch plan results batch:", error);
          requests.forEach((request) => {
            const planPeriodId = makePlanPeriodId(
              request.plan_id as PlanId,
              request.period as Period
            );
            fetchedKeysRef.current.delete(planPeriodId);
          });
        });
    }
  }, [dispatch, planPeriodKeys, tenantName, getPlanResultsBatch]);

  const filteredLoadedPlanResults = React.useMemo(
    () =>
      typeSafeObjectFromEntries(
        typeSafeObjectEntries(loadedPlanResults).filter(
          (arg): arg is [PlanPeriodId, PlannedResultsRead] =>
            arg[1] !== undefined
        )
      ),
    [loadedPlanResults]
  );

  return Object.values(loadedPlanResults).some(
    (planResults) => planResults === undefined
  ) || preview === null ? (
    <EditableTableSkeleton />
  ) : (
    <EditableDeployFormContentWrapper
      preview={preview}
      planId={planId}
      setPlanId={setPlanId}
      materials={materials}
      steelGrades={steelGrades}
      planPeriodKeys={planPeriodKeys}
      loadedPlanResults={filteredLoadedPlanResults}
    />
  );
};

const EditableDeployFormContentWrapper = ({
  preview,
  planId,
  setPlanId,
  materials,
  steelGrades,
  planPeriodKeys,
  loadedPlanResults,
}: ContentWrapperProps) => {
  const dispatch = useAppDispatch();
  const mixQuantity = useMixQuantity();

  const initializedPeriodsRef = React.useRef(new Set());

  React.useEffect(() => {
    planPeriodKeys.forEach((planPeriodId) => {
      const loadedData = loadedPlanResults[planPeriodId];
      const [planId, period] = parsePlanPeriodId(planPeriodId);

      if (loadedData && !initializedPeriodsRef.current.has(planPeriodId)) {
        initializedPeriodsRef.current.add(planPeriodId);
        const periodResults =
          loadedData.periods[period.toString()]?.mixes ?? [];
        dispatch(
          planMixTableEditsSlice.actions.set([
            planId,
            period,
            makeRows(planId, period, periodResults, steelGrades, mixQuantity),
          ])
        );
      }
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- loadedPlanResults is an object ref that may be unstable; we deliberately re-run only on the planPeriodKeys/planId/steelGrades/mixQuantity changes that warrant rebuilding the row state
  }, [dispatch, planPeriodKeys, planId, steelGrades, mixQuantity]);

  const periodPlanEdits = useAppSelector((state) =>
    selectPeriodPlanEdits(state, planPeriodKeys)
  );
  const filteredPeriodPlanEdits = React.useMemo(
    () => filterPeriodPlanEdits(periodPlanEdits),
    [periodPlanEdits]
  );

  return Object.values(periodPlanEdits).some((edits) => edits === undefined) ? (
    <EditableTableSkeleton />
  ) : (
    <EditableDeployFormContent
      preview={preview}
      planId={planId}
      setPlanId={setPlanId}
      materials={materials}
      steelGrades={steelGrades}
      loadedPlanResults={loadedPlanResults}
      periodPlanEdits={filteredPeriodPlanEdits}
    />
  );
};

const unpackResultsAndEdits = (
  loadedPlanResults: Record<PlanPeriodId, PlannedResultsRead>,
  periodPlanEdits: Record<PlanPeriodId, PlanMixTableEdits>
) => {
  const results = typeSafeObjectFromEntries(
    typeSafeObjectEntries(loadedPlanResults).map(
      ([planPeriodId, planResults]) => {
        const [, period] = parsePlanPeriodId(planPeriodId);
        return [
          planPeriodId,
          planResults.periods[period.toString()]?.mixes ?? [],
        ];
      }
    )
  );

  const allPeriodMaterialSummaries = typeSafeObjectFromEntries(
    typeSafeObjectEntries(loadedPlanResults).map(
      ([planPeriodId, planResults]) => [
        planPeriodId,
        planResults.summary.period_material_summary,
      ]
    )
  );
  const { isSubmitting, originalRows, editedRows } =
    unpackEdits(periodPlanEdits);
  return {
    results,
    allPeriodMaterialSummaries,
    isSubmitting,
    originalRows,
    editedRows,
  };
};

const EditableDeployFormContent = ({
  preview,
  planId,
  setPlanId,
  materials,
  steelGrades,
  loadedPlanResults,
  periodPlanEdits,
}: ContentProps) => {
  const tenantName = useTenant();
  const { currentData: previewPlan } = useGetPlanQuery({
    tenantName,
    planId: preview.planId,
  });
  const {
    results,
    allPeriodMaterialSummaries,
    isSubmitting,
    originalRows,
    editedRows,
  } = React.useMemo(
    () => unpackResultsAndEdits(loadedPlanResults, periodPlanEdits),
    [loadedPlanResults, periodPlanEdits]
  );
  const focussedEditedRows = React.useMemo(
    () => editedRows[makePlanPeriodId(preview.planId, preview.period)] ?? [],
    [preview, editedRows]
  );
  const focussedSteelGrades = React.useMemo(() => {
    const thisPlanIds = new Set(
      focussedEditedRows.flatMap((row) =>
        row.steelGrades.map((steelGrade) => steelGrade.id)
      )
    );
    return steelGrades.filter((steelGrade) => thisPlanIds.has(steelGrade.id));
  }, [focussedEditedRows, steelGrades]);

  const selectedSteelGradeIds = useAppSelector((state) =>
    selectSearchedSteelGradeIds(state, preview.planId, preview.period, "deploy")
  );
  const hasCleanupOverrideFlag = useIsBehindFeatureFlag("cleanup_override");
  const { manual_heat_overrides } = useTenantData();
  const mixIdsRequiringNameOverride = React.useMemo(
    () =>
      preview.isBackup
        ? new Set<number>()
        : new Set(
            getMixIdsRequiringMixCodeOverride(
              previewPlan?.mix_metadata ?? [],
              preview.period,
              manual_heat_overrides
            )
          ),
    [manual_heat_overrides, preview.isBackup, preview.period, previewPlan]
  );
  const nameOverrideOptions =
    (hasCleanupOverrideFlag || mixIdsRequiringNameOverride.size > 0) &&
    !preview.isBackup
      ? (manual_heat_overrides ?? [])
      : [];
  const mixGrouping = useMixGrouping();
  const periodMaterialSummaries =
    allPeriodMaterialSummaries[
      makePlanPeriodId(preview.planId, preview.period)
    ]!;

  const inventoryByMaterialName = React.useMemo(() => {
    return typeSafeObjectFromEntries(
      periodMaterialSummaries
        .filter((summary) => summary.period === preview.period)
        .map((summary) => [
          summary.material,
          summary.initial_inventory + summary.obtainable_obtained,
        ])
    );
  }, [periodMaterialSummaries, preview.period]);
  const tableMaterials = React.useMemo(() => {
    return materials
      .filter((material) => material.addition_location === "basket")
      .sort(
        (materialA, materialB) =>
          materialA.deployment_ordering - materialB.deployment_ordering
      )
      .map((material) => ({
        id: material.id,
        name: material.name,
        inventory: inventoryByMaterialName[material.name] ?? null,
      }));
  }, [materials, inventoryByMaterialName]);
  const driInventory = materials.some(
    (material) => material.addition_location === "hopper"
  )
    ? materials
        .filter((material) => material.addition_location === "hopper")
        .reduce(
          (total, material) =>
            total + (inventoryByMaterialName[material.name] ?? 0),
          0
        )
    : null;

  return (
    <Stack>
      <TableWrapper
        formPlanId={planId}
        focussedPlanId={preview.planId}
        focussedPeriod={preview.period}
        setPlanId={setPlanId}
        materials={materials}
        allSteelGrades={steelGrades}
        focussedSteelGrades={focussedSteelGrades}
        results={results}
        isSubmitting={isSubmitting}
        originalRows={originalRows}
        editedRows={editedRows}
        focussedEditedRows={focussedEditedRows}
      >
        {({
          onChange,
          isSubmitting,
          hasEdited,
          rows,
          massScaleFactor,
          isPercentageMode,
          popoverLayerEditor,
          collapse,
        }) => (
          <DeployTable
            planId={preview.planId}
            period={preview.period}
            disableEdit={isSubmitting || preview.isBackup}
            hasEdited={hasEdited}
            rows={rows}
            materials={tableMaterials}
            selectedSteelGradeIds={selectedSteelGradeIds}
            onChange={onChange}
            mixGrouping={mixGrouping}
            driInventory={driInventory}
            steelGradesWidth={130}
            massScaleFactorOverride={massScaleFactor}
            isPercentageModeOverride={isPercentageMode}
            popoverLayerEditor={popoverLayerEditor}
            nameOverrideOptions={nameOverrideOptions}
            mixIdsRequiringNameOverride={mixIdsRequiringNameOverride}
            collapse={collapse}
          />
        )}
      </TableWrapper>
    </Stack>
  );
};

const TableWrapper = ({
  formPlanId,
  focussedPlanId,
  focussedPeriod,
  setPlanId,
  materials,
  focussedSteelGrades,
  results,
  children,
  isSubmitting,
  originalRows,
  editedRows,
  focussedEditedRows,
}: TableWrapperProps) => {
  const dispatch = useAppDispatch();
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const searchId = useSearchId();
  const { default_mix_breakdown, small_mass_per_mass, layer_edit_mode } =
    useTenantData();
  const isLayerEditEnabled = layer_edit_mode !== "none";
  const [comment, setComment] = React.useState("");
  const mixQuantity = useMixQuantity();
  const units = useUnitsFormatter(false);
  const collapse = useMixCollapse(focussedPlanId, focussedPeriod);

  const [displayMode, setDisplayMode] = React.useState<
    "mass_fraction" | "mass"
  >(() => {
    return default_mix_breakdown === "percentage" ? "mass_fraction" : "mass";
  });

  const baseMassScaleFactor =
    default_mix_breakdown === "small_mass" ? small_mass_per_mass : 1;
  const massScaleFactor =
    displayMode === "mass_fraction"
      ? PERCENTAGE_SCALE_FACTOR
      : baseMassScaleFactor;
  const isPercentageMode = displayMode === "mass_fraction";

  const hasEdited = typeSafeObjectKeys(originalRows).some(
    (key) => !equal(editedRows[key], originalRows[key])
  );

  const handleOnTableChange = React.useCallback(
    (planId: PlanId, period: Period, editedRows: Row[]) => {
      dispatch(
        planMixTableEditsSlice.actions.update([planId, period, editedRows])
      );
    },
    [dispatch]
  );

  const {
    showingLayers,
    layerPreviewData,
    hasLayerEdits,
    mixIdsWithEdits,
    handleShowLayers,
    handleSave,
    handleDiscard,
    handleLayerStateChange,
    handleMixLayerStateChange,
    handleShowWeights,
    getLayerStateForMix,
    resetMixLayers,
  } = useLayerEditor({
    focussedPlanId,
    focussedPeriod,
    formPlanId,
    results,
    tenantName,
    searchId,
    setPlanId,
    comment,
    setComment,
    layerEditMode: layer_edit_mode,
  });

  const [doSummarisePlanResultsMutation] = useSummarisePlanResultsMutation({
    fixedCacheKey: makePlanPeriodId(focussedPlanId, focussedPeriod),
  });

  React.useEffect(() => {
    const mutation = doSummarisePlanResultsMutation({
      tenantName,
      unsummarisedPlanResults: {
        plan_id: focussedPlanId,
        period: focussedPeriod,
        mixes: tableRowsToBackendMixes(focussedEditedRows).map((mix) => ({
          ...mix,
          layer_source: mixIdsWithEdits.has(mix.original_mix_id)
            ? "manual"
            : "per_basket_totals",
        })),
      },
    });
    return () => {
      mutation.abort();
    };
  }, [
    doSummarisePlanResultsMutation,
    focussedEditedRows,
    tenantName,
    focussedPlanId,
    focussedPeriod,
    mixIdsWithEdits,
  ]);

  const popoverLayerEditor = React.useMemo(
    () =>
      isLayerEditEnabled
        ? {
            baseMassScaleFactor,
            materials,
            getLayerStateForMix,
            onMixLayerStateChange: handleMixLayerStateChange,
            isMixManuallyLayered: (mixId: number) => mixIdsWithEdits.has(mixId),
            onResetMixLayers: resetMixLayers,
          }
        : undefined,
    [
      isLayerEditEnabled,
      baseMassScaleFactor,
      materials,
      getLayerStateForMix,
      handleMixLayerStateChange,
      mixIdsWithEdits,
      resetMixLayers,
    ]
  );

  return (
    <>
      <Box
        sx={{
          width: "100%",
          my: 1,
          alignItems: "center",
          gap: 1,
          display: "grid",
          height: 40,
          gridTemplateColumns: isLayerEditEnabled
            ? "minmax(min-content, 1fr) repeat(5, min-content)"
            : "minmax(min-content, 1fr) repeat(4, min-content)",
        }}
      >
        <Stack direction="row" gap={1}>
          {!showingLayers && (
            <>
              <SteelGradesSelect
                view="deploy"
                planId={focussedPlanId}
                period={focussedPeriod}
                steelGrades={focussedSteelGrades}
              />
              {mixQuantity === "mass" && (
                <ToggleButtonGroup
                  size="small"
                  value={displayMode}
                  exclusive
                  onChange={(_, newMode: "mass_fraction" | "mass" | null) => {
                    if (newMode) setDisplayMode(newMode);
                  }}
                >
                  <ToggleButton value="mass">
                    {units("mass", false)}
                  </ToggleButton>
                  <ToggleButton value="mass_fraction">
                    {units("mass_fraction", false)}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </>
          )}
        </Stack>
        <CommentCell onCommentChange={setComment} />
        <Box>
          <Tooltip
            title={<PlanAncestorsTooltip planId={focussedPlanId} />}
            placement="bottom"
            arrow
            enterDelay={100}
            leaveDelay={500}
            slotProps={{
              popper: {
                sx: {
                  "& .MuiTooltip-tooltip": {
                    bgcolor: "background.paper",
                    color: "text.primary",
                    p: 0,
                    maxWidth: "none",
                  },
                },
              },
            }}
          >
            <IconButton size="small">
              <HistoryRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {isLayerEditEnabled && (
          <ToggleButtonGroup
            size="small"
            value={showingLayers ? "layers" : "mixes"}
            exclusive
            disabled={isSubmitting}
            onChange={(_, value: "mixes" | "layers" | null) => {
              if (value === "mixes") handleShowWeights();
              else if (value === "layers") handleShowLayers();
            }}
          >
            <ToggleButton value="mixes">{t("mixes")}</ToggleButton>
            <ToggleButton value="layers">{t("layers")}</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Button
          disabled={(!hasEdited && !hasLayerEdits) || isSubmitting}
          onClick={handleDiscard}
          color="primary"
          variant="text"
          sx={{ whiteSpace: "nowrap" }}
        >
          {t("discardChanges")}
        </Button>
        <LoadingButton
          loading={isSubmitting}
          disabled={!hasEdited && !hasLayerEdits}
          onClick={() => void handleSave()}
          color="primary"
          variant="contained"
          sx={{ whiteSpace: "nowrap" }}
        >
          {t("saveChanges")}
        </LoadingButton>
      </Box>
      {layerPreviewData ? (
        <Box sx={{ display: showingLayers ? "contents" : "none" }}>
          <LayerEditor
            initialState={layerPreviewData}
            renderMixSummary={(mixId) =>
              renderMixPriceComparison({
                planId: focussedPlanId,
                period: focussedPeriod,
                mixId,
                originalMixes:
                  results[makePlanPeriodId(focussedPlanId, focussedPeriod)] ??
                  [],
              })
            }
            materials={materials}
            onStateChange={handleLayerStateChange}
            weightsEditable={isLayerEditEnabled}
            massScaleFactor={baseMassScaleFactor}
            manuallyLayeredMixIds={mixIdsWithEdits}
            onResetMixLayers={resetMixLayers}
            collapsedMixIds={collapse.collapsedMixIds}
            onToggleCollapse={collapse.onToggleCollapse}
          />
        </Box>
      ) : showingLayers ? (
        <Skeleton variant="rectangular" height={400} animation="wave" />
      ) : null}
      <Box sx={{ display: showingLayers ? "none" : "contents" }}>
        {focussedEditedRows === undefined
          ? null
          : children({
              onChange: handleOnTableChange,
              isSubmitting,
              hasEdited,
              rows: focussedEditedRows,
              massScaleFactor,
              isPercentageMode,
              popoverLayerEditor,
              collapse,
            })}
      </Box>
    </>
  );
};
